import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PermissionType } from '@/graphql/generated/types.generated';
import type { MappedFeature } from '../utils/map-graphql-to-calculator.util';

interface FeatureRowProps {
    feature: MappedFeature;
}

export const FeatureRow = ({ feature }: FeatureRowProps) => {
    const { t } = useTranslation();
    const renderValue = () => {
        if (feature.type === PermissionType.Boolean) {
            return feature.value ? (
                <CheckRounded sx={{ fontSize: 18, color: 'success.main' }} />
            ) : (
                <CloseRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
            );
        }

        if (feature.type === PermissionType.Numeric) {
            return (
                <Typography variant="body2" fontWeight={600} color="text.primary">
                    {feature.displayValue}
                </Typography>
            );
        }

        // Enum: no option included in this plan
        if (!feature.displayValue) {
            return <CloseRounded sx={{ fontSize: 18, color: 'text.disabled' }} />;
        }

        return (
            <Chip
                label={feature.displayValue}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.72rem', height: 22 }}
            />
        );
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
                py: 1.25,
                px: 2,
                borderRadius: 2,
                '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
            }}
        >
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    variant="body2"
                    color={
                        feature.type === PermissionType.Boolean && feature.value === 0
                            ? 'text.disabled'
                            : 'text.primary'
                    }
                >
                    {t(feature.label)}
                </Typography>
                {feature.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {t(feature.description)}
                    </Typography>
                )}
            </Box>
            {renderValue()}
        </Stack>
    );
};
